/**
 * GTM GA4 E-commerce Event Tracking
 * Handles GA4 e-commerce events based on window.gtmConfig flags
 *
 * Supported Events:
 * - view_item: Product page views
 * - view_item_list: Collection/category page views
 * - add_to_cart: Add to cart actions
 * - view_cart: Cart page views
 * - purchase: Order completions
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

// Check if GTM is enabled
// @ts-ignore - GTM config is injected by Liquid template
if (!window.gtmConfig || !window.gtmConfig.enabled) {
  console.log('GTM events disabled');
} else {
  /**
   * Debug logger - only logs when debug mode is enabled
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  function gtmDebugLog(event, data) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.debugMode) {
      console.log(`[GTM Debug] ${event}:`, data);
    }
  }

  /**
   * Clear previous ecommerce data before pushing new event
   * Recommended by Google to avoid data pollution
   */
  function clearEcommerceData() {
    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push({ ecommerce: null });
  }

  function getAffiliation(product) {
    const cpabs = Math.abs(product.cp);
    const price = product.price;
    const livetv = product.livetv ? product.livetv : false;
    // 1. Check boolean first
    if (livetv) return 'livetv';

    // 2. Cache the absolute value
    const cpAbs = Math.abs(product.cp);

    // 3. Handle the CP logic
    if (cpAbs > 0) {
      return cpAbs === product.price ? 'FPC' : 'Clearance';
    }

    return 'FPC';
  };



  /**
   * View Item Event (Product Page)
   * Fires when a user views a product detail page
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item
   */
  function fireViewItemEvent() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItem) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'product') return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.product) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const product = window.gtmConfig.product;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = window.gtmConfig.currency || 'USD';

    const affiliation = getAffiliation(product);

    clearEcommerceData();

    const eventData = {
      event: 'view_item',
      // Product-level tracking data
      ecommerce: {
        currency: currency,
        value: parseFloat(product.price) || 0,
        items: [
          {
            item_id: String(product.item_id),
            item_name: product.item_name,
            affiliation: affiliation,
            item_brand: product.item_brand || '',
            item_category: product.item_category || '',
            item_variant: product.item_variant || '',
            price: parseFloat(product.price) || 0,
            quantity: 1,
            masterProductId: String(product.masterProductId),
            item_image_url: product.item_image_url || '',

          },
        ],
      },
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.addEventListener("load", () => window.dataLayer.push(eventData));
    gtmDebugLog('view_item', eventData);
  }

  /**
   * View Item List Event (Collection Page)
   * Fires when a user views a list of products
   * Includes both GA4 view_item_list event and legacy category tracking data
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_list
   */
  function fireViewItemListEvent() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItemList) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'collection') return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.collection) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const collection = window.gtmConfig.collection;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    // Calculate total value of visible items
    //const totalValue = collection.items.reduce((sum, item) => {
    //  return sum + (parseFloat(item.price) || 0);
    //}, 0);

    const eventData = {
      event: 'view_item_list',
      // Legacy category tracking data
      ecomm_pagetype: 'category',
      ecomm_category: collection.title,
      categoryID: String(collection.id),
      categoryName: collection.title,
      //categoryimageURL: collection.imageUrl || '',
      // GA4 E-commerce data
      //ecommerce: {
      //  currency: currency,
      //  item_list_id: String(collection.id),
      //  item_list_name: collection.title,
      //  items: collection.items.map((item, index) => ({
      //    item_id: String(item.item_id),
      //    item_name: item.item_name,
      //    item_brand: item.item_brand || '',
      //    item_category: item.item_category || collection.title,
      //    item_variant: item.item_variant || '',
      //    item_list_id: String(collection.id),
      //    item_list_name: collection.title,
      //    index: item.index || index + 1,
      //    price: parseFloat(item.price) || 0,
      //  })),
      //  },
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.addEventListener("load", () => window.dataLayer.push(eventData));
    gtmDebugLog('view_item_list', eventData);
  }

  /**
   * View Cart Event (Cart Page)
   * Fires when a user views the cart page
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_cart
   */
  function fireViewCartEvent() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewCart) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'cart') return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.cart) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const cart = window.gtmConfig.cart;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    const eventData = {
      event: 'view_cart',
      ecommerce: {
        currency: currency,
        value: parseFloat(cart.value) || 0,
        items: cart.items.map((item, index) => ({
          item_id: String(item.item_id),
          item_name: item.item_name,
          affiliation: getAffiliation(item),
          masterProductId: item.masterProductId,
          item_image_url: String(item.item_image_url),
          item_brand: item.item_brand || '',
          item_category: item.item_category || '',
          item_variant: item.item_variant || '',
          price: parseFloat(item.price) || 0,
          quantity: item.quantity || 1,
          index: index + 1,
        })),
      },
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.addEventListener("load", () => window.dataLayer.push(eventData));
    gtmDebugLog('view_cart', eventData);
  }

  /**
   * Add to Cart Event
   * Fires when a user adds an item to their cart
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart
   * @param {Object} productData - Product data object
   * @param {string|number} productData.masterProductId - Shopify product ID
   * @param {string|number} productData.item_id - Variant SKU
   * @param {string} productData.item_name - Product name
   * @param {string} [productData.item_brand] - Product brand/vendor
   * @param {string} [productData.item_category] - Product category
   * @param {string} [productData.item_variant] - Variant title
   * @param {string} [productData.item_image_url] - Product image URL
   * @param {number} productData.price - Product price
   * @param {number} [productData.quantity] - Quantity added
   * @param {string} [productData.currency] - Currency code
   */
  function trackAddToCart(productData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.addToCart) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = productData.currency || window.gtmConfig.currency || 'USD';
    const quantity = productData.quantity || 1;
    const price = parseFloat(productData.price) || 0;
    const value = price * quantity;
    const affiliation = getAffiliation(productData);
    console.log(productData);


    clearEcommerceData();

    const eventData = {
      event: 'add_to_cart',
      // Product-level tracking data
      ecommerce: {
        currency: currency,
        value: value,
        items: [
          {
            item_id: String(productData.item_id),
            affiliation: affiliation,
            item_name: productData.item_name || '',
            item_brand: productData.item_brand || '',
            item_category: productData.item_category || '',
            item_variant: productData.item_variant || '',
            item_image_url: productData.item_image_url || '',
            price: price,
            quantity: quantity,
            masterProductId: String(productData.masterProductId || productData.item_id),
            item_image_url: productData.item_image_url || '',
          },
        ],
      },
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(eventData);
    gtmDebugLog('add_to_cart', eventData);
  }

  /**
   * Remove from Cart Event
   * Fires when a user removes an item from their cart
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#remove_from_cart
   * @param {Object} productData - Product data object
   */
  function trackRemoveFromCart(productData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.addToCart) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = productData.currency || window.gtmConfig.currency || 'USD';
    const quantity = productData.quantity || 1;
    const price = parseFloat(productData.price) || 0;
    const value = price * quantity;

    clearEcommerceData();

    const eventData = {
      event: 'remove_from_cart',
      ecommerce: {
        currency: currency,
        value: value,
        items: [
          {
            item_id: String(productData.item_id),
            item_name: productData.item_name || '',
            item_brand: productData.item_brand || '',
            item_category: productData.item_category || '',
            item_variant: productData.item_variant || '',
            price: price,
            quantity: quantity,
          },
        ],
      },
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(eventData);
    gtmDebugLog('remove_from_cart', eventData);
  }

  /**
   * Purchase Event
   * Fires when a user completes a purchase
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
   * @param {Object} orderData - Order data object
   * @param {string} orderData.transaction_id - Transaction/Order ID
   * @param {number} orderData.value - Order total value
   * @param {string} [orderData.currency] - Currency code
   * @param {number} [orderData.tax] - Tax amount
   * @param {number} [orderData.shipping] - Shipping cost
   * @param {string} [orderData.coupon] - Coupon code used
   * @param {Array} orderData.items - Array of order items
   */
  function trackPurchase(orderData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.purchaseEvent) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = orderData.currency || window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    const eventData = {
      event: 'purchase',
      ecommerce: {
        transaction_id: String(orderData.transaction_id),
        value: parseFloat(orderData.value) || 0,
        currency: currency,
        tax: parseFloat(orderData.tax) || 0,
        shipping: parseFloat(orderData.shipping) || 0,
        coupon: orderData.coupon || '',
        items: (orderData.items || []).map((item, index) => ({
          item_id: String(item.item_id || item.id),
          item_name: item.item_name || item.name || '',
          item_brand: item.item_brand || item.brand || '',
          item_category: item.item_category || item.category || '',
          item_variant: item.item_variant || item.variant || '',
          price: parseFloat(item.price) || 0,
          quantity: item.quantity || 1,
          index: index + 1,
        })),
      },
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(eventData);
    gtmDebugLog('purchase', eventData);
  }

  /**
   * Banner Tracking Event
   * Tracks banner impressions and clicks
   * @param {string} eventType - Event type (impression, click)
   * @param {Object} bannerData - Banner data object
   */
  function trackBannerEvent(eventType, bannerData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.bannerTracking) return;

    const eventData = {
      event: `banner_${eventType}`,
      banner_id: bannerData.id || '',
      banner_name: bannerData.name || '',
      banner_position: bannerData.position || '',
      banner_url: bannerData.url || '',
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(eventData);
    gtmDebugLog(`banner_${eventType}`, eventData);
  }

  /**
   * BID Event Tracking
   * Tracks BID (Browser ID) events
   * @param {Object} bidData - BID data object
   */
  function trackBidEvent(bidData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.bidEvent) return;

    const eventData = {
      event: 'bid_event',
      bid_id: bidData.id || '',
      bid_timestamp: bidData.timestamp || new Date().getTime(),
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(eventData);
    gtmDebugLog('bid_event', eventData);
  }

  /**
   * Select Item Event (Product Click from List)
   * Fires when a user clicks on a product in a list
   * @param {Object} productData - Product data
   * @param {string} [listId] - List/Collection ID
   * @param {string} [listName] - List/Collection name
   */
  function trackSelectItem(productData, listId, listName) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItemList) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = productData.currency || window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    const eventData = {
      event: 'select_item',
      ecommerce: {
        currency: currency,
        item_list_id: listId || '',
        item_list_name: listName || '',
        items: [
          {
            item_id: String(productData.item_id),
            item_name: productData.item_name || '',
            item_brand: productData.item_brand || '',
            item_category: productData.item_category || '',
            item_variant: productData.item_variant || '',
            item_list_id: listId || '',
            item_list_name: listName || '',
            index: productData.index || 1,
            price: parseFloat(productData.price) || 0,
          },
        ],
      },
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(eventData);
    gtmDebugLog('select_item', eventData);
  }

  /**
   * Handle variant change on PDP
   * Fires view_item event when user switches to a different variant
   * @param {Object} variant - Shopify variant object from the event
   * @param {string|number} variant.id - Variant ID
   * @param {string} [variant.sku] - Variant SKU
   * @param {string} [variant.title] - Variant title
   * @param {number} [variant.price] - Variant price in cents
   * @param {Object} [variant.featured_media] - Variant featured media
   * @param {Object} [variant.featured_media.preview_image] - Preview image object
   * @param {string} [variant.featured_media.preview_image.src] - Image source URL
   */
  function handleVariantChange(variant) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItem) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'product') return;
    if (!variant) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const productConfig = window.gtmConfig.product;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = window.gtmConfig.currency || 'USD';

    if (!productConfig) return;

    // Calculate price (Shopify returns price in cents)
    const price = variant.price ? variant.price / 100 : parseFloat(productConfig.price) || 0;

    // Get variant image URL from featured_media (this theme's structure)
    const variantImageUrl =
      variant.featured_media?.preview_image?.src ||
      variant.featured_image?.src ||
      productConfig.item_image_url ||
      '';

    // Get variant title
    const variantTitle = variant.title || variant.name || variant.option1 || '';
    console.log("dvab ,yeah variant just changed");
    const getAffiliation = (product) => {
      const cpabs = Math.abs(product.cp);
      const finalprice = price;
      const livetv = product.livetv ? product.livetv : false;
      // 1. Check boolean first
      if (livetv) return 'livetv';

      // 2. Cache the absolute value
      const cpAbs = Math.abs(product.cp);

      // 3. Handle the CP logic
      if (cpAbs > 0) {
        return cpAbs === product.price ? 'FPC' : 'Clearance';
      }

      return 'FPC';
    };

    const affiliation = getAffiliation(productConfig);


    clearEcommerceData();

    const eventData = {
      event: 'view_item',
      // Product-level tracking data
      ecommerce: {
        currency: currency,
        value: price,
        items: [
          {
            item_id: variant.sku || String(variant.id),
            item_name: productConfig.item_name,
            affiliation: affiliation,
            item_brand: productConfig.item_brand || '',
            item_category: productConfig.item_category || '',
            item_variant: variantTitle || '',
            price: parseFloat(productConfig.price) || 0,
            quantity: 1,
            masterProductId: String(productConfig.masterProductId),
            item_image_url: variantImageUrl || '',
          },

        ],
      },
    };

    // Update the gtmConfig.product with new variant data for add_to_cart tracking
    // @ts-ignore - GTM config is injected by Liquid template
    window.gtmConfig.product = {
      ...productConfig,
      item_id: variant.sku || String(variant.id),
      item_variant: variantTitle,
      item_image_url: variantImageUrl,
      price: price,
      sku: variant.sku || '',
    };

    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(eventData);
    gtmDebugLog('view_item (variant change)', eventData);
  }

  /**
   * Listen for variant change events on product pages
   * This theme uses ThemeEvents.variantUpdate ('variant:update') from events.js
   * Only listen to variant:update to avoid duplicate events
   */
  function setupVariantChangeListener() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'product') return;

    // Track the last variant ID to prevent duplicate events
    let lastTrackedVariantId = null;

    // Listen for variant:update event (this theme's primary event)
    // Event contains: resource (variant object), sourceId, data (html, productId)
    document.addEventListener('variant:update', function (event) {
      // @ts-ignore - event detail from theme
      const detail = event.detail;
      const variant = detail?.resource;

      // Only track if variant exists and is different from last tracked
      if (variant && variant.id !== lastTrackedVariantId) {
        lastTrackedVariantId = variant.id;
        handleVariantChange(variant);
      }
    });

    gtmDebugLog('Variant change listener', { status: 'initialized' });
  }

  /**
   * Listen for cart:update events from the theme
   * This captures add_to_cart events fired by product-form-component
   */
  function setupCartEventListener() {

    document.addEventListener('cart:update', function (event) {
      // @ts-ignore - event detail from theme
      const detail = event.detail;
      //console.log('dvab ,',detail);

      // Only track successful add to cart (not errors, not cart updates from quantity changes)
      if (detail?.data?.source === 'product-form-component' && !detail?.data?.didError) {
        // Get product data from the page if available
        // @ts-ignore - GTM config is injected by Liquid template
        const productConfig = window.gtmConfig.product;

        if (productConfig) {
          trackAddToCart({
            masterProductId: productConfig.masterProductId,
            item_id: productConfig.item_id,
            item_name: productConfig.item_name,
            item_brand: productConfig.item_brand,
            item_category: productConfig.item_category,
            item_variant: productConfig.item_variant,
            item_image_url: productConfig.item_image_url,
            cp: productConfig.cp,
            livetv: productConfig.livetv,
            price: productConfig.price,
            quantity: detail.data.itemCount || 1,
          });
        } else if (detail.data.productId) {
          
          // Fallback: minimal data from event
          // trackAddToCart({
          //   masterProductId: 'mini' + detail.data.productId,
          //   item_id: 'mini' + detail.data.productId,
          //   item_name: 'mini',
          //   price: 0,
          //   quantity: detail.data.itemCount || 1,
          // });
        }
      }
    });

    gtmDebugLog('Cart event listener', { status: 'initialized' });
  }

  /**
   * Fire page view events based on page type
   * Called on DOMContentLoaded and SPA navigation
   */
  function firePageViewEvents() {
    // @ts-ignore - GTM config is injected by Liquid template
    const pageType = window.gtmConfig.pageType;

    gtmDebugLog('Page view', { pageType: pageType });

    switch (pageType) {
      case 'product':
        fireViewItemEvent();
        break;
      case 'collection':
        fireViewItemListEvent();
        break;
      case 'cart':
        fireViewCartEvent();
        break;
    }
  }

  /**
   * Initialize all GTM event tracking
   */
  function initGTMEvents() {
    // Setup cart event listener for add_to_cart tracking
    setupCartEventListener();

    // Setup variant change listener for PDP
    setupVariantChangeListener();

    // Fire initial page view events
    firePageViewEvents();

    // fire add to cart plp
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('button[ref="addToCartButton"]');
      if (!btn) return;
    
      const container = btn.closest('add-to-cart-component');
      if (!container) return;
    
      let productConfig;
      try {
        productConfig = JSON.parse(container.dataset.gtmProduct);
      } catch (err) {
        console.error('Failed to parse gtmProduct:', err);
        return;
      }
    
      trackAddToCart({
        masterProductId: productConfig.masterProductId,
        item_id:         productConfig.item_id,
        item_name:       productConfig.item_name,
        item_brand:      productConfig.item_brand,
        item_category:   productConfig.item_category,
        item_variant:    productConfig.item_variant,
        item_image_url:  productConfig.item_image_url,
        cp:              productConfig.cp,
        livetv:          productConfig.livetv,
        price:           productConfig.price,
        quantity:        1,
      });
    });

    gtmDebugLog('GTM Events', { status: 'initialized' });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGTMEvents);
  } else {
    initGTMEvents();
  }

  // Listen for Shopify section updates (SPA navigation)
  document.addEventListener('shopify:section:load', firePageViewEvents);

  // Expose tracking functions globally for use in other scripts
  // @ts-ignore - Global GTM tracking object
  window.gtmTrack = {
    // GA4 E-commerce events
    addToCart: trackAddToCart,
    removeFromCart: trackRemoveFromCart,
    selectItem: trackSelectItem,
    purchase: trackPurchase,

    // Additional events
    banner: trackBannerEvent,
    bid: trackBidEvent,

    // Manual page view triggers (for SPA navigation)
    viewItem: fireViewItemEvent,
    viewItemList: fireViewItemListEvent,
    viewCart: fireViewCartEvent,

    // Variant change handler (for PDP)
    variantChange: handleVariantChange,

    // Utility
    debug: gtmDebugLog,
  };
}
